import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useCRUD from "../../hooks/useCRUD";
import Spinner from "../Spinner/Spinner";
import { formatDateWithDayShortDay } from "../../utils/dateUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faCarSide,
  faClock,
  faEnvelope,
  faLocationDot,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import Pagination from "../Elements/Pagination";

const RIDES_PER_PAGE = 3;

const firstName = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)[0] || name;

const UpcomingRides = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const {
    data: upcomingRides,
    isLoading,
    fetchItems: getRides,
    error,
  } = useCRUD("/api/upcoming-events-with-rides");

  useEffect(() => {
    getRides().catch((err) => {
      console.error("Error fetching events:", err);
    });
  }, [getRides]);

  if (isLoading) return <Spinner />;
  if (error) return <p>Could not load upcoming rides: {error.message}</p>;
  if (!upcomingRides || upcomingRides.length === 0) {
    return (
      <div className="card bg-white shadow-lg mx-auto p-6 rounded-xl">
        <h2 className="text-xl font-black text-gray-900 mb-2">
          No Upcoming Rides Scheduled
        </h2>
        <p className="text-sm text-gray-500">
          Rides will appear here when upcoming events have assigned pickups.
        </p>
      </div>
    );
  }

  const totalPages = Math.max(
    1,
    Math.ceil(upcomingRides.length / RIDES_PER_PAGE),
  );
  const page = Math.min(currentPage, totalPages);
  const pagedRides = upcomingRides.slice(
    (page - 1) * RIDES_PER_PAGE,
    page * RIDES_PER_PAGE,
  );

  const getStatusClass = (status) => {
    switch (status) {
      case "On-Route":
        return "bg-warning text-white"; // Update these classes as needed
      case "Arrived":
        return "bg-success text-white"; // Update these classes as needed
      default:
        return "bg-gray-100 text-gray-800"; // Default state
    }
  };

  return (
    <div className="card bg-white shadow-lg mx-auto p-5 sm:p-6 rounded-xl">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
            Ride coordination
          </p>
          <h2 className="text-xl font-black text-gray-900 sm:text-2xl">
            Active Rides
          </h2>
        </div>
        <span className="rounded-md bg-[#eaf3fa] px-3 py-1 text-xs font-bold text-[#004a7e]">
          {upcomingRides.length} events
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {pagedRides.map((event) => (
          <div
            key={event.id}
            className="overflow-hidden rounded-lg border border-gray-100 bg-gray-50 p-4"
          >
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="truncate text-lg font-black text-gray-900">
                  {event.title}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#004a7e]">
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                  {formatDateWithDayShortDay(event.dateOfOccurrence)}
                </p>
              </div>
              <Link
                to={`/event/manage/${event.id}`}
                className="inline-flex items-center justify-center rounded-lg bg-[#004a7e] px-3 py-2 text-xs font-bold text-white hover:bg-[#003a64]"
              >
                Manage
              </Link>
            </div>
            <div className="space-y-3">
              {event.event_rides.map((ride, index) => (
                <div
                  key={index}
                  className="relative overflow-hidden rounded-lg border border-gray-100 bg-white px-3 pb-3 pt-8 shadow-sm"
                >
                  <span
                    className={`absolute left-0 top-0 rounded-br-lg px-3 py-1 text-xs font-bold ${getStatusClass(
                      ride.pickupStatus,
                    )}`}
                  >
                    {ride.pickupStatus || "Pending"}
                  </span>
                  <span className="absolute right-0 top-0 rounded-bl-lg bg-orange-100 px-3 py-1 text-xs font-bold text-orange-800 shadow-sm">
                    <FontAwesomeIcon icon={faCarSide} className="mr-1" />
                    {firstName(ride.driver)}
                  </span>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-gray-900">
                        {ride.name}
                        {ride.messageForAdmin && (
                          <span
                            className="tooltip ml-2 text-[#004a7e]"
                            data-tip={ride.messageForAdmin}
                          >
                            <FontAwesomeIcon icon={faEnvelope} />
                          </span>
                        )}
                        {ride.noOfPpl > 1 && (
                          <span className="ml-2 inline-block rounded-md bg-[#004a7e] px-2 py-0.5 text-xs text-white">
                            <FontAwesomeIcon icon={faUsers} /> {ride.noOfPpl}
                          </span>
                        )}
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        <FontAwesomeIcon
                          icon={faLocationDot}
                          className="mr-2"
                        />
                        {ride.pickupLocation || "No pickup location"}
                      </p>
                      {ride.pickupTime && (
                        <span className="mt-2 inline-flex rounded-md bg-gray-100 px-2 py-1 text-xs font-bold text-gray-700">
                          <FontAwesomeIcon icon={faClock} className="mr-1" />
                          {ride.pickupTime}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Pagination
        currentPage={page}
        totalItems={upcomingRides.length}
        itemsPerPage={RIDES_PER_PAGE}
        onPageChange={setCurrentPage}
        label="ride events"
      />
    </div>
  );
};

export default UpcomingRides;
